import React, { useEffect, useState } from 'react';
import { useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { apiUrl } from '../config/apiBase';
import { FaExclamationTriangle } from 'react-icons/fa';
import './PaymentLoading.css';

const PaymentLoading = () => {
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const planType = searchParams.get('plan');
  const isYearly = searchParams.get('yearly') === 'true';

  // Function to navigate to pricing section
  const navigateToPricing = () => {
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  useEffect(() => {
    const processPayment = async () => {
      // Wait for auth to complete
      if (authLoading) {
        return;
      }

      if (!user) {
        return;
      }

      if (!planType) {
        setError('No plan specified');
        return;
      }

      try {

        // Fetch price configuration
        const priceResponse = await fetch(apiUrl('/api/stripe/prices-config'));
        if (!priceResponse.ok) {
          throw new Error('Failed to fetch prices');
        }
        const priceConfig = await priceResponse.json();

        // Get the correct price ID
        const priceId = isYearly ? 
          priceConfig.standard.yearly : 
          priceConfig.standard.monthly;

        if (!priceId) {
          throw new Error('Price ID not found');
        }

        // Create checkout session
        const response = await fetch(apiUrl('/api/stripe/create-checkout-session'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            priceId,
            userId: user.id,
            email: user.email,
            isYearly
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create checkout session');
        }

        // Redirect to Stripe checkout
        window.location.href = data.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setError(error.message);
      }
    };

    // Add a small delay to ensure user state is stable
    const timer = setTimeout(() => {
      processPayment();
    }, 1000);

    return () => clearTimeout(timer);
  }, [user, planType, isYearly, retryCount, authLoading]);

  // If auth is still loading, show loading
  if (authLoading) {
    return (
      <div className="payment-loading-container">
        <div className="payment-loading-card">
          <div className="loading-spinner"></div>
          <h2>Authenticating...</h2>
          <p>Please wait while we verify your account.</p>
        </div>
      </div>
    );
  }

  // If no user and auth is complete, redirect to home
  if (!user && !authLoading) {
    return <Navigate to="/" replace />;
  }

  // If there's an error, show retry option
  if (error) {
    return (
      <div className="payment-loading-container">
        <div className="payment-loading-card error">
          <div className="icon-wrapper">
            <FaExclamationTriangle className="react-icon error-icon-color" />
          </div>
          <h2>Payment Setup Failed</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              onClick={() => {
                setError(null);
                setRetryCount(prev => prev + 1);
              }}
              className="retry-button"
            >
              Try Again
            </button>
            <button 
              onClick={navigateToPricing}
              className="back-button"
            >
              Back to Pricing
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  return (
    <div className="payment-loading-container">
      <div className="payment-loading-card">
        <div className="loading-spinner"></div>
        <h2>Setting up your payment...</h2>
        <p>Please wait while we redirect you to secure payment processing.</p>
        <div className="loading-details">
          <p>Plan: <strong>Standard {isYearly ? 'Yearly' : 'Monthly'}</strong></p>
          <p>Price: <strong>{isYearly ? '$100/year' : '$10/month'}</strong></p>
        </div>
      </div>
    </div>
  );
};

export default PaymentLoading;